import type { BarChartSeries } from '@mantine/charts'
import { BarChart } from '@mantine/charts'
import { Center, Paper, Text, Title } from '@mantine/core'
import { useMemo } from 'react'

import { colors } from '@/configs/colors'
import type * as T from '@/services/api/api.types'
import { formatBillingDate } from '@/utils/analytics'

// Constants
const CHART_HEIGHT = 350
const CHART_TITLE = 'Daily Credit Usage for All Users'
const Y_AXIS_LABEL = 'Credits'

const billingSeries: BarChartSeries[] = [
  {
    name: 'activeCredits',
    label: 'Daily Usage',
    color: colors.chartColors[0],
  },
]

interface BillingChartProps {
  data: T.GetBillingCreditsDataResponse
  startDate: string
  endDate: string
}

export default function BillingChart({ data, startDate, endDate }: BillingChartProps) {
  const chartData = useMemo(() => {
    const { credits = [] } = data

    // Handle empty data case
    if (credits.length === 0) {
      return []
    }

    // Generate complete date range
    const generateDateRange = (start: string, end: string): string[] => {
      const dates: string[] = []
      const currentDate = new Date(start)
      const endDate = new Date(end)

      while (currentDate <= endDate) {
        dates.push(currentDate.toISOString().split('T')[0])
        currentDate.setDate(currentDate.getDate() + 1)
      }

      return dates
    }

    const dataMap = new Map(credits.map(d => [new Date(d.date).toISOString().split('T')[0], d.activeCredits]))

    // Fill in missing dates with 0 values
    const allDates = generateDateRange(startDate, endDate)
    return allDates.map(date => ({
      date,
      activeCredits: dataMap.get(date) || 0,
    }))
  }, [data, startDate, endDate])

  const maxDailyCredits = useMemo(() => {
    return chartData.length > 0 ? Math.max(...chartData.map(d => d.activeCredits)) : 0
  }, [chartData])

  // Handle empty data case
  if (chartData.length === 0) {
    return (
      <Paper radius="md" p="lg" shadow="sm" px="xl">
        <Title order={6} mb="md">
          {CHART_TITLE}
        </Title>
        <Center h={CHART_HEIGHT}>
          <Text c="dimmed">No data available for this period</Text>
        </Center>
      </Paper>
    )
  }

  return (
    <Paper radius="md" p="lg" shadow="sm" px="xl">
      <Title order={6} mb="xl">
        {CHART_TITLE}
      </Title>
      <BarChart
        h={CHART_HEIGHT}
        data={chartData}
        dataKey="date"
        series={billingSeries}
        gridProps={{ horizontal: false, vertical: false }}
        xAxisProps={{
          tickFormatter: (value: any) => formatBillingDate(value),
          angle: -45,
          textAnchor: 'end',
          axisLine: { stroke: '#000', strokeWidth: 1 },
          interval: 0,
          domain: [startDate, endDate],
        }}
        yAxisProps={{
          label: { value: Y_AXIS_LABEL, angle: -90, position: 'insideLeft' },
          domain: [0, maxDailyCredits + 1],
          axisLine: { stroke: '#000', strokeWidth: 1 },
        }}
        tooltipProps={{
          labelFormatter: (label: any) => formatBillingDate(label),
          formatter: (value: number | null, name: string) => {
            if (value === null) return null
            return name === 'activeCredits'
              ? [`${value.toLocaleString()} credits`, 'Daily Usage']
              : [`${value.toLocaleString()} credits`, name]
          },
        }}
      />
    </Paper>
  )
}
